/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import { Oval } from "react-loader-spinner";
import moment from "moment";
import toast from "react-hot-toast";

import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import {
  getSmartRoofOwesReport,
  bulkMarkPaid,
  resetBulkMarkPaid,
} from "../../reportsSlice";
import { InvoiceItem } from "./InvoiceItem";
import { DateRangePicker } from "../../../../components";
import { PaymentType } from "../../../../models";
import { downloadCsv } from "../../../../lib/csvExport";

import "./SmartRoofOwesReportView.scss";

type SortField = "price" | "date";
type SortDirection = "asc" | "desc";

export const SmartRoofOwesReportView: React.FC = () => {
  const dispatch = useAppDispatch();

  const [selectedInvoiceIds, setSelectedInvoiceIds] = React.useState<
    Set<string>
  >(new Set());
  const [dateFrom, setDateFrom] = React.useState<moment.Moment>(moment());
  const [dateTo, setDateTo] = React.useState<moment.Moment>(moment());
  const [sortField, setSortField] = React.useState<SortField>("date");
  const [sortDirection, setSortDirection] =
    React.useState<SortDirection>("desc");

  const getSmartRoofOwesReportAPI = useAppSelector(
    (state) => state.reports.getSmartRoofOwesReportAPI
  );
  const smartRoofOwesReport = useAppSelector(
    (state) => state.reports.smartRoofOwesReport
  );
  const bulkMarkPaidAPI = useAppSelector(
    (state) => state.reports.bulkMarkPaidAPI
  );

  React.useEffect(() => {
    getSmartRoofOwesReportData();
    // Clear selections when date range changes
    setSelectedInvoiceIds(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo]);

  React.useEffect(() => {
    if (!bulkMarkPaidAPI.loading && !!bulkMarkPaidAPI.error) {
      toast.error(
        "Something went wrong marking invoices as paid. Please try again."
      );
    }

    if (
      !bulkMarkPaidAPI.loading &&
      !bulkMarkPaidAPI.error &&
      bulkMarkPaidAPI.value
    ) {
      const markedCount = selectedInvoiceIds.size;
      toast.success(
        `Successfully marked ${markedCount} invoice${
          markedCount !== 1 ? "s" : ""
        } as paid.`
      );

      // Clear selections
      setSelectedInvoiceIds(new Set());

      // Refetch report
      getSmartRoofOwesReportData();

      // Reset bulk call
      dispatch(resetBulkMarkPaid());
    }
  }, [
    bulkMarkPaidAPI.loading,
    !!bulkMarkPaidAPI.error,
    !!bulkMarkPaidAPI.value,
    selectedInvoiceIds.size,
    dispatch,
  ]);

  const getSmartRoofOwesReportData = () => {
    dispatch(
      getSmartRoofOwesReport({
        fromDate: moment(dateFrom).format("YYYY-MM-DD"),
        toDate: moment(dateTo).format("YYYY-MM-DD"),
      })
    );
  };

  const handleDownloadCsv = () => {
    const from = moment(dateFrom).format("YYYY-MM-DD");
    const to = moment(dateTo).format("YYYY-MM-DD");
    downloadCsv(
      `smartroof-owes-${from}_to_${to}.csv`,
      [
        { header: "Name", value: (i) => i.name },
        { header: "Order ID", value: (i) => i.orderId },
        { header: "Type", value: (i) => i.type },
        { header: "Total", value: (i) => i.total.toFixed(2) },
        { header: "Date", value: (i) => i.date },
        { header: "Address", value: (i) => i.address },
      ],
      sortedInvoices
    );
  };

  const handleInvoiceSelect = (invoiceId: string, isSelected: boolean) => {
    setSelectedInvoiceIds((prev) => {
      const newSet = new Set(prev);
      if (isSelected) {
        newSet.add(invoiceId);
      } else {
        newSet.delete(invoiceId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (!smartRoofOwesReport) return;

    const allInvoiceIds = sortedInvoices.map((invoice) => invoice.invoiceId);
    setSelectedInvoiceIds(new Set(allInvoiceIds));
  };

  const handleDeselectAll = () => {
    setSelectedInvoiceIds(new Set());
  };

  const handleMarkPaid = () => {
    const selectedIds = Array.from(selectedInvoiceIds);
    const selectedCount = selectedIds.length;
    const selectedTotalFormatted = selectedTotal.toFixed(2);

    const confirmMessage = `Are you sure you want to mark ${selectedCount} invoice${
      selectedCount !== 1 ? "s" : ""
    } as paid for a total of $${selectedTotalFormatted}?`;

    if (window.confirm(confirmMessage)) {
      dispatch(
        bulkMarkPaid({
          ids: selectedIds,
          paymentType: PaymentType.ACH,
        })
      );
    }
  };

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      // Toggle direction if same field
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Set new field with default direction
      setSortField(field);
      setSortDirection(field === "price" ? "desc" : "desc");
    }
  };

  const sortedInvoices = React.useMemo(() => {
    if (!smartRoofOwesReport?.invoices) return [];

    const invoices = [...smartRoofOwesReport.invoices];

    return invoices.sort((a, b) => {
      let comparison = 0;

      if (sortField === "price") {
        comparison = a.total - b.total;
      } else if (sortField === "date") {
        const dateA = moment(a.date || a.date || a.date);
        const dateB = moment(b.date || b.date || b.date);
        comparison = dateA.valueOf() - dateB.valueOf();
      }

      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [smartRoofOwesReport?.invoices, sortField, sortDirection]);

  const selectedInvoices =
    sortedInvoices.filter((invoice) =>
      selectedInvoiceIds.has(invoice.invoiceId)
    ) || [];

  const selectedTotal = selectedInvoices.reduce(
    (sum, invoice) => sum + invoice.total,
    0
  );

  const areAllSelected =
    sortedInvoices.length > 0 &&
    selectedInvoiceIds.size === sortedInvoices.length;

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return "";
    return sortDirection === "asc" ? "↑" : "↓";
  };

  return (
    <div className="smartroof-owes-report-view-component">
      <div className="title-container">
        <div className="title-row">
          <div className="title-text">SmartRoof Owes Report</div>
          {smartRoofOwesReport && (
            <div className="title-count">
              {smartRoofOwesReport.invoices.length}
            </div>
          )}
          {getSmartRoofOwesReportAPI.loading && (
            <div style={{ marginLeft: 8 }}>
              <Oval
                color="#131313"
                height={20}
                width={20}
                secondaryColor="#0A60BB"
              />
            </div>
          )}
          <button
            className="download-csv-button"
            onClick={handleDownloadCsv}
            disabled={
              !smartRoofOwesReport ||
              smartRoofOwesReport.invoices.length === 0
            }
          >
            Download CSV
          </button>
        </div>

        <DateRangePicker
          fromDate={dateFrom}
          setFromDate={setDateFrom}
          toDate={dateTo}
          setToDate={setDateTo}
        />
      </div>

      {smartRoofOwesReport && smartRoofOwesReport.invoices.length > 0 && (
        <>
          <div className="controls-container">
            <div className="selection-controls">
              <label className="select-all-checkbox">
                <input
                  type="checkbox"
                  checked={areAllSelected}
                  onChange={
                    areAllSelected ? handleDeselectAll : handleSelectAll
                  }
                />
                <span className="checkmark"></span>
                Select All
              </label>
            </div>

            <div className="sort-controls">
              <span className="sort-label">Sort by:</span>
              <button
                className={`sort-button ${
                  sortField === "price" ? "active" : ""
                }`}
                onClick={() => handleSort("price")}
              >
                Price {getSortIcon("price")}
              </button>
              <button
                className={`sort-button ${
                  sortField === "date" ? "active" : ""
                }`}
                onClick={() => handleSort("date")}
              >
                Date {getSortIcon("date")}
              </button>
            </div>
          </div>
        </>
      )}

      {selectedInvoiceIds.size > 0 && (
        <div className="selection-summary">
          <div className="summary-info">
            <span className="selected-count">
              {selectedInvoiceIds.size} item
              {selectedInvoiceIds.size !== 1 ? "s" : ""} selected
            </span>
            <span className="selected-total">
              Total: ${selectedTotal.toFixed(2)}
            </span>
          </div>

          <button
            className="mark-paid-button"
            onClick={handleMarkPaid}
            disabled={selectedInvoiceIds.size === 0 || bulkMarkPaidAPI.loading}
          >
            {bulkMarkPaidAPI.loading ? <>Processing...</> : "Mark Paid"}
          </button>
        </div>
      )}

      <div className="invoices-container">
        {sortedInvoices.map((invoice) => (
          <InvoiceItem
            key={invoice.invoiceId}
            invoice={invoice}
            isSelected={selectedInvoiceIds.has(invoice.invoiceId)}
            onSelect={handleInvoiceSelect}
          />
        ))}
      </div>
    </div>
  );
};
